import { useTalkPageContext } from 'components/pages/talks/slug/talk-page-context';
import { host } from 'f';
import { useEffect, useRef, useState } from 'react';
import shallow from 'zustand/shallow';
import useVideoPlayerStore from '../store';
import useTimedYouTube from './use-timed-youtube';

const getYouTubeDomain = () => {
  if (typeof window === 'undefined') return 'www.youtube-nocookie.com';
  const hasTargetingConsent = window.__userHasConsentedToTargetingCookies;
  return hasTargetingConsent ? 'www.youtube.com' : 'www.youtube-nocookie.com';
};

const options = {
  width: '100%',
  height: '100%',
  playerVars: {
    controls: 1,
    showinfo: 0,
    origin: host,
    enablejsapi: 1,
    iv_load_policy: 3,
    modestbranding: 1,
    playsinline: 1
  }
};

const YouTubePlayer = ({ youtubeID, playerTalkData, ...props }) => {
  const { autoplay } = useTalkPageContext();
  const [isAPIReady, setIsAPIReady] = useState(false);
  const playerRef = useRef(null);
  const [youtubeDomain, setYoutubeDomain] = useState(getYouTubeDomain());

  useEffect(() => {
    const handleConsent = () => {
      setYoutubeDomain(getYouTubeDomain());
    };

    handleConsent();
    window.addEventListener('OnetrustActiveGroupsLoaded', handleConsent);
    return () => {
      window.removeEventListener('OnetrustActiveGroupsLoaded', handleConsent);
    };
  }, []);

  const videoPlayerActions = useVideoPlayerStore(
    state => ({
      autoplayed: state.autoplayed,
      onAd: state.onAd,
      onComplete: state.onComplete,
      onDuration: state.onDuration,
      onPause: state.onPause,
      onPlay: state.onPlay,
      onReady: state.onReady,
      onCurrentTime: state.onCurrentTime,
      onPlaybackMode: state.onPlaybackMode,
      onTalk: state.onTalk,
      registerYouTubePlayer: state.registerYouTubePlayer
    }),
    shallow
  );

  const { onReady: onYouTubeReady, ...videoActions } = useTimedYouTube({
    onReady: videoPlayerActions.onReady,
    ...videoPlayerActions
  });

  const shouldAutoplay = autoplay || videoPlayerActions?.autoplayed;

  useEffect(() => {
    const token = `YOUTUBE-${Date.now().toString(36)}`;
    videoPlayerActions.onAd({ token });
  }, []);

  useEffect(() => {
    videoPlayerActions.onTalk(playerTalkData);
  }, [playerTalkData.id]);

  useEffect(() => {
    // Load the IFrame Player API code asynchronously.
    const loadYouTubeAPI = () => {
      const tag = document.createElement('script');
      tag.src = `https://${youtubeDomain}/iframe_api`;
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    };

    if (!window.YT && youtubeDomain) {
      loadYouTubeAPI();
    }

    const onYouTubeIframeAPIReady = () => {
      setIsAPIReady(true);
    };

    if (window.YT && window.YT.Player) {
      onYouTubeIframeAPIReady();
    } else {
      window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
    }
  }, [youtubeDomain]);

  useEffect(() => {
    if (!isAPIReady || !youtubeDomain) return;

    const player = new window.YT.Player(playerRef.current, {
      ...options,
      videoId: youtubeID,
      host: `https://${youtubeDomain}`,
      playerVars: {
        ...options.playerVars,
        autoplay: shouldAutoplay ? 1 : 0,
        mute: shouldAutoplay ? 1 : 0
      },
      events: {
        onReady: onYouTubeReady,
        onStateChange: event => {
          if (event.data === window.YT.PlayerState.PLAYING) {
            videoPlayerActions.onPlay();
          } else if (event.data === window.YT.PlayerState.PAUSED) {
            videoActions.onPause();
          } else if (event.data === window.YT.PlayerState.ENDED) {
            props.onEnded();
          }
        }
      }
    });

    videoPlayerActions.registerYouTubePlayer(player);
  }, [isAPIReady, youtubeID, youtubeDomain]);

  if (!isAPIReady) {
    return null;
  }

  return <div ref={playerRef} id="youtube-player" />;
};

export default YouTubePlayer;
